import { action, observable, computed } from 'mobx';
import { emit, events } from 'elc-service-bus';
import { BrowserStorage } from 'elc-universal-utils';
import { VtoBaseController, IVtoWindow } from './VtoBaseController';
import {
    IVtoSkinCareConfig,
    IVtoSkinCareTranslations
} from '../../exported/vto-skin-care/VtoSkinCare';
import { IFeature } from '../constants/Features';
import * as Constants from '../constants/VtoSkinCare';
import {
    IVtoSkinConcernScores,
    ISkinCareProductList,
    ISite,
    IContentApiParams,
    IProductGridItem,
    IProductBenefitTypes,
    IResultNodeBlocks
} from '../interfaces/IVtoSkinCare';
import { QuizApiRepository } from '../repositories/QuizApiRepository';
import { serviceNames, diContainer } from '../../service-setup/diContainer';
import { IQuizParam } from '../../api/QuizApiSdk';
import { VtoQuiz } from '../domain/entities/VtoQuiz';
import { IMutationObserver, IVtoFlag } from '../interfaces/IVto';

declare let window: IVtoWindow;
declare let site: ISite;

interface IVtoInitializeSkinCareController {
    config: IVtoSkinCareConfig;
    vtoSkinCareRef: React.RefObject<HTMLDivElement>;
    translations: IVtoSkinCareTranslations;
}

export class VtoSkinCareViewController extends VtoBaseController {
    @observable public isVtoSkinCareEnabled: boolean = false;
    @observable public isVtoSkinCareCtaEnabled: boolean = true;
    @observable public isVtoSkinCareOpened: boolean = false;
    @observable public isVtoSkinAnalysisFinished: boolean = false;
    @observable public isVtoSkinCareResultInProgress: boolean = false;
    @observable public isVtoSkinCareResultSectionEnabled: boolean = false;
    @observable public skinCareProductList: ISkinCareProductList[] = [];
    @observable public quizApiQuery: IQuizParam;
    @observable public vtoQuizResult: VtoQuiz;
    @observable public isResultCtaFixed: boolean = true;
    @observable public isSkinCareNodeResultEnabled: boolean = false;

    public translations: IVtoSkinCareTranslations;
    public skinCareResultProductItems: IProductGridItem[] = [];
    public skinCareResultProductBenefitTypes: IProductBenefitTypes = {};
    public skinCareResultNodeBlocks: IResultNodeBlocks = {};

    private quizApiRepository: QuizApiRepository;
    private vtoSkinCareConfig: IFeature;
    private vtoSkinCareFeature?: IVtoFlag;
    private vtoSkinConcernScores: IVtoSkinConcernScores;
    private vtoSkinCareQuizId: string;
    private isVtoResultEnabled: boolean;
    private editorialNodeId: number;
    private vtoSkinCareProductIds: string[];
    private vtoSkinCareBenefitTypes: string[];
    private finalProductIds: string[];
    private finalBenefitTypes: string[];
    private debounceTimeout: number;
    private vtoSkinCareRef: React.RefObject<HTMLDivElement>;
    private skinCareContentResultPromises: Promise<string>[];
    private skinCareContentResultKeys: string[];
    private productGridRef: React.RefObject<HTMLDivElement>;

    @action
    public initialize = (props: IVtoInitializeSkinCareController) => {
        const { config, translations, vtoSkinCareRef } = props;
        const {
            features,
            vtoSkinCareQuizId,
            isVtoResultEnabled,
            skinCareEditorialNodeId,
            vtoFeature
        } = config;
        this.vtoSkinCareConfig = features.vtoSkinCare;
        this.vtoSkinCareFeature = vtoFeature && vtoFeature.skincare;
        this.quizApiRepository = diContainer.get(serviceNames.quizApiRepository);
        this.vtoSkinCareQuizId = vtoSkinCareQuizId;
        this.isVtoResultEnabled = isVtoResultEnabled;
        this.editorialNodeId = skinCareEditorialNodeId;
        this.translations = translations;
        this.vtoSkinCareRef = vtoSkinCareRef;
        this.updateVtoSkinCareState();
    };

    @computed
    get data() {
        return {
            isVtoSkinCareCtaEnabled: this.isVtoSkinCareCtaEnabled,
            isVtoSkinCareEnabled: this.isVtoSkinCareEnabled,
            isVtoSkinCareOpened: this.isVtoSkinCareOpened,
            isVtoSkinAnalysisFinished: this.isVtoSkinAnalysisFinished,
            isVtoSkinCareResultInProgress: this.isVtoSkinCareResultInProgress,
            isVtoSkinCareResultSectionEnabled: this.isVtoSkinCareResultSectionEnabled,
            isResultCtaFixed: this.isResultCtaFixed,
            isSkinCareNodeResultEnabled: this.isSkinCareNodeResultEnabled
        };
    }

    @action
    public updateVtoSkinCareState = () => {
        this.isVtoSkinCareEnabled = !!(
            this.vtoSkinCareConfig &&
            this.vtoSkinCareConfig.enabled &&
            this.vtoSkinCareFeature &&
            this.vtoSkinCareFeature.enabled
        );
    };

    public loaded = async () => {
        window.addEventListener('scroll', this.handleScroll);
    };

    public initVtoSkinCare = () => {
        if (typeof window.ymkAsyncInit !== 'function') {
            const { apiUrl, apiKey } = this.vtoSkinCareConfig;
            this.loadVto(apiUrl, apiKey);
            this.declareVtoSkincareCallback();
        }
        window.ymkAsyncInit();
        this.toggleVtoSkinCareCtaState(false);
        emit(events.VTO_SKIN_CARE_OPENED);
    };

    @action
    public toggleVtoSkinCareCtaState = (isEnabled: boolean) => {
        this.isVtoSkinCareCtaEnabled = isEnabled;
    };

    @action
    public toggleVtoSkinCareOpenedState = (isOpened: boolean) => {
        this.isVtoSkinCareOpened = isOpened;
    };

    @action
    public toggleVtoSkinAnalysisState = (isFinished: boolean) => {
        this.isVtoSkinAnalysisFinished = isFinished;
    };

    @action
    public toggleVtoSkinResultProgress = (isEnabled: boolean) => {
        this.isVtoSkinCareResultInProgress = isEnabled;
    };

    @action
    public toggleSkinCareResultSection = (isEnabled: boolean) => {
        this.isVtoSkinCareResultSectionEnabled = isEnabled;
    };

    @action
    public toggleResultCtaFixedPosition = (isFixed: boolean) => {
        this.isResultCtaFixed = isFixed;
    };

    @action
    public enableSkinCareNodeResult = () => {
        this.isSkinCareNodeResultEnabled = true;
    };

    public vtoSkinCareStartOver = () => {
        window.YMK.close();
        this.toggleSkinCareResultSection(false);
    };

    public showVtoSkinCareResult = async () => {
        this.scrollToVtoSkinCare();
        this.toggleVtoSkinResultProgress(true);
        this.processVtoProductResult();
        this.filterVtoSkinConcernScores();
        await this.updateQuizApiResult();
        this.preprocessSkinCareProductResult();
        this.toggleSkinCareResultSection(true);
        this.toggleVtoSkinResultProgress(false);
    };

    private scrollToVtoSkinCare() {
        const vtoSkinCareRef = this.vtoSkinCareRef;
        const { SCROLL_BEHAVIOR_SMOOTH, SCROLL_BLOCK_START } = Constants;
        if (vtoSkinCareRef && vtoSkinCareRef.current) {
            vtoSkinCareRef.current.scrollIntoView({
                behavior: SCROLL_BEHAVIOR_SMOOTH,
                block: SCROLL_BLOCK_START
            });
        }
    }

    private declareVtoSkincareCallback() {
        window.ymkAsyncInit = () => {
            if (typeof window.YMK !== 'undefined') {
                const vtoSkinCareParams = this.getVtoSkinCareParams();
                window.YMK.init(vtoSkinCareParams);
                window.YMK.openSkincare(Constants.SKIN_CARE_MODE);
                this.addVtoSkinCareEventListeners();
            }
        };
    }

    private getVtoSkinCareParams() {
        const iframeHeight = this.getIframeHeight();
        const { language } = this.vtoSkinCareConfig;

        return {
            autoOpen: false,
            showCloseBtnOnLauncher: true,
            height: iframeHeight,
            ...(language && { language })
        };
    }

    private getIframeHeight() {
        const { IFRAME_MIN_HEIGHT } = Constants;
        const innerHeight = window.innerHeight;
        const stickyHeight = (this.vtoSkinCareFeature && this.vtoSkinCareFeature.stickyHeight) || 0;
        const availableHeight = innerHeight - stickyHeight;
        const iframeHeight =
            availableHeight < IFRAME_MIN_HEIGHT ? IFRAME_MIN_HEIGHT : availableHeight;

        return iframeHeight;
    }

    // TODO: SPPSELTO-3026 - Check feasibility of moving VTO event listeners to common place
    private addVtoSkinCareEventListeners() {
        if (typeof window.YMK !== 'object' || this.eventListenerIdentifiers.length) {
            return;
        }

        Object.values(Constants.Events).forEach((event) => {
            const eventListenerIdentifier = window.YMK.addEventListener(event, (result) => {
                this.vtoSkinCareHandlers[event](result as IVtoSkinConcernScores);
            });
            this.eventListenerIdentifiers.push(eventListenerIdentifier);
        });
    }

    private vtoSkinCareHandlers: {
        [key in Constants.Events]: (result: IVtoSkinConcernScores) => void;
    } = {
        [Constants.Events.OPENED]: () => this.handleVtoSkinCareOpenedEvent(),
        [Constants.Events.CAMERA_OPENED]: () => emit(events.VTO_SKIN_CARE_CAMERA_OPENED),
        [Constants.Events.CAMERA_FAILED]: () => emit(events.VTO_SKIN_CARE_CAMERA_FAILED),
        [Constants.Events.CAMERA_CLOSED]: () => emit(events.VTO_SKIN_CARE_CAMERA_CLOSED),
        [Constants.Events.LOADING]: () => emit(events.VTO_SKIN_CARE_LOADING),
        [Constants.Events.LOADED]: () => emit(events.VTO_SKIN_CARE_LOADED),
        [Constants.Events.VIDEO_LOADED]: () => emit(events.VTO_SKIN_CARE_VIDEO_LOADED),
        [Constants.Events.SKIN_ANALYSIS_STARTED]: () => this.handleVtoSkinAnalysisStartedEvent(),
        [Constants.Events.SKIN_ANALYSIS_FINISHED]: (result) =>
            this.handleVtoSkinAnalysisFinishedEvent(result),
        [Constants.Events.SKIN_BACK_TO_DETECTING]: () => emit(events.VTO_SKIN_BACK_TO_DETECTING),
        [Constants.Events.CLOSED]: () => this.handleVtoSkinCareClosedEvent()
    };

    private handleVtoSkinCareOpenedEvent() {
        emit(events.VTO_SKIN_CARE_OPENED);
        this.toggleVtoSkinCareOpenedState(true);
    }

    private handleVtoSkinCareClosedEvent() {
        emit(events.VTO_SKIN_CARE_CLOSED);
        this.toggleVtoSkinCareCtaState(true);
        this.toggleVtoSkinCareOpenedState(false);
        this.toggleVtoSkinAnalysisState(false);
        this.removeVtoEventListeners();
    }

    private handleVtoSkinAnalysisStartedEvent() {
        emit(events.VTO_SKIN_ANALYSIS_STARTED);
        this.toggleVtoSkinAnalysisState(false);
    }

    private handleVtoSkinAnalysisFinishedEvent(result: IVtoSkinConcernScores) {
        this.vtoSkinConcernScores = result;
        emit(events.VTO_SKIN_ANALYSIS_FINISHED);
        this.toggleVtoSkinAnalysisState(true);
    }

    private processVtoProductResult() {
        window.YMK.getSkincareRecommendationList((skinCareProductList: ISkinCareProductList[]) => {
            this.vtoSkinCareProductIds = [];
            this.vtoSkinCareBenefitTypes = [];
            Object.values(skinCareProductList).forEach((value) => {
                this.vtoSkinCareProductIds.push(value.productId);
                this.vtoSkinCareBenefitTypes.push(value.category);
            });
        });
    }

    // Refer https://confluence.esteeonline.com/display/GSBA/EL+YouCam+Skincare+VTO for this logic
    private filterVtoSkinConcernScores() {
        const vtoSkinConcernScores = { ...this.vtoSkinConcernScores };
        const { FaceConcerns } = Constants;

        vtoSkinConcernScores[
            FaceConcerns.linesAndWrinkles
        ] = this.getForeheadAndLowerFaceWrinkleAvg(vtoSkinConcernScores);

        const sortedSkinConcernScores = Object.entries(vtoSkinConcernScores).sort(function (a, b) {
            return b[1] - a[1];
        });

        const queryParams = this.getQueryParams(sortedSkinConcernScores);

        this.quizApiQuery = { queryParams: queryParams };
    }

    private getForeheadAndLowerFaceWrinkleAvg = (vtoSkinConcernScores: IVtoSkinConcernScores) => {
        const { OtherConcerns } = Constants;

        return (
            (vtoSkinConcernScores[OtherConcerns.wrinkleForehead] +
                vtoSkinConcernScores[OtherConcerns.wrinkleLowerFace]) /
            2
        );
    };

    private getQueryParams(skinConcernScores: [string, number][]) {
        const {
            FACE_CONCERN_PARAM,
            EYE_CONCERN_PARAM,
            SKIN_TYPE_PARAM,
            SKIN_TYPE_VALUE,
            QuizApiConcerns
        } = Constants;

        return skinConcernScores.reduce(
            (acc, [skinConcern, score]) => {
                if (this.existsAndFaceArrayIsLessThanTwo(skinConcern, acc[FACE_CONCERN_PARAM])) {
                    const faceConcernKey = this.getFaceConcernKey(skinConcern);

                    acc[FACE_CONCERN_PARAM].push(
                        QuizApiConcerns[faceConcernKey as keyof typeof QuizApiConcerns]
                    );

                    return acc;
                }

                if (
                    this.existsAndEyeConcernArrayIsLessThanOne(skinConcern, acc[EYE_CONCERN_PARAM])
                ) {
                    const eyeConcernKey = this.getEyeConcernKey(skinConcern);

                    acc[EYE_CONCERN_PARAM].push(
                        QuizApiConcerns[eyeConcernKey as keyof typeof QuizApiConcerns]
                    );

                    return acc;
                }

                return acc;
            },
            {
                [FACE_CONCERN_PARAM]: Array(),
                [EYE_CONCERN_PARAM]: Array(),
                [SKIN_TYPE_PARAM]: SKIN_TYPE_VALUE
            }
        );
    }

    private existsAndFaceArrayIsLessThanTwo = (skinConcern: string, FaceArray: string[]) => {
        const faceConcernValues = Object.values(Constants.FaceConcerns);

        return (
            faceConcernValues.indexOf(skinConcern as Constants.FaceConcerns) !== -1 &&
            FaceArray.length < 2
        );
    };

    private getFaceConcernKey = (skinConcern: string) => {
        const { FaceConcerns } = Constants;
        const faceConcernKeys = Object.keys(FaceConcerns);

        return faceConcernKeys.find(
            (key) => FaceConcerns[key as keyof typeof FaceConcerns] === skinConcern
        );
    };

    private existsAndEyeConcernArrayIsLessThanOne = (skinConcern: string, EyeArray: string[]) => {
        const eyeConcernValues = Object.values(Constants.EyeConcerns);

        return (
            eyeConcernValues.indexOf(skinConcern as Constants.EyeConcerns) !== -1 &&
            EyeArray.length < 1
        );
    };

    private getEyeConcernKey = (skinConcern: string) => {
        const { EyeConcerns } = Constants;
        const eyeConcernKeys = Object.keys(EyeConcerns);

        return eyeConcernKeys.find(
            (key) => EyeConcerns[key as keyof typeof EyeConcerns] === skinConcern
        );
    };

    private async updateQuizApiResult() {
        const vtoQuizResult: VtoQuiz[] = await this.quizApiRepository.getQuizApiResult(
            this.quizApiQuery,
            this.vtoSkinCareQuizId
        );
        this.vtoQuizResult = {} as VtoQuiz;
        if (vtoQuizResult.length === 0) {
            return;
        }
        this.vtoQuizResult = { ...vtoQuizResult[0] };
    }

    private preprocessSkinCareProductResult() {
        this.updateProductSource();
        this.preprocessProductIDs();
        this.preprocessBenefitTypes();
    }

    private updateProductSource() {
        this.finalProductIds = this.isVtoResultEnabled
            ? this.vtoSkinCareProductIds
            : this.vtoQuizResult.productIds
            ? this.vtoQuizResult.productIds.split(',')
            : [];
        this.finalBenefitTypes = this.isVtoResultEnabled
            ? this.vtoSkinCareBenefitTypes
            : this.vtoQuizResult.productBenefitTypes
            ? this.vtoQuizResult.productBenefitTypes.split(',')
            : [];
    }

    private async preprocessSkinCareNodeResult() {
        this.skinCareContentResultPromises = [];
        this.skinCareContentResultKeys = [];
        this.pushResultNodesToPromise();
        this.pushEditorialNodeToPromise();
        const data = await Promise.all(this.skinCareContentResultPromises);
        this.handleSkinCareContentResults(data);
    }

    private pushResultNodesToPromise() {
        Object.entries(this.vtoQuizResult).forEach(([key, value]) => {
            if (typeof value === 'number' && !this.skinCareResultNodeBlocks[key]) {
                this.skinCareContentResultPromises.push(this.getNodeContent(value));
                this.skinCareContentResultKeys.push(key);
            }
        });
    }

    private pushEditorialNodeToPromise() {
        const { EDITORIAL_NODE_KEY } = Constants;
        if (this.editorialNodeId && !this.skinCareResultNodeBlocks[EDITORIAL_NODE_KEY]) {
            this.skinCareContentResultPromises.push(this.getNodeContent(this.editorialNodeId));
            this.skinCareContentResultKeys.push(EDITORIAL_NODE_KEY);
        }
    }

    private handleSkinCareContentResults(data: string[]) {
        this.skinCareContentResultKeys.forEach((skinCareContentResultKey, index) => {
            this.skinCareResultNodeBlocks[skinCareContentResultKey] = data[index];
        });
        this.enableSkinCareNodeResult();
    }

    private async getNodeContent(value: number) {
        if (typeof site.elcContentApi === 'undefined') {
            return '';
        }
        const {
            DEFAULT_LOCALE,
            DRUPAL_API_TOKEN,
            DRUPAL_API_FIELDS,
            REVISION_TAG_COOKIE,
            LOCALE_COOKIE
        } = Constants;
        const params: IContentApiParams = {
            revision_tag: BrowserStorage.getCookie(REVISION_TAG_COOKIE) || '',
            locale: BrowserStorage.getCookie(LOCALE_COOKIE) || DEFAULT_LOCALE,
            node: value,
            key: DRUPAL_API_TOKEN,
            field: DRUPAL_API_FIELDS
        };
        const data = await site.elcContentApi.getData(params, true);

        return data.rendered;
    }

    private preprocessProductIDs() {
        const { SKU_ITEM, PRODUCT_ITEM } = Constants;
        const productType = this.isVtoResultEnabled ? SKU_ITEM : PRODUCT_ITEM;
        this.skinCareResultProductItems = this.finalProductIds.reduce(
            (acc: IProductGridItem[], item: string) => {
                acc.push({ type: productType, params: { id: item } });

                return acc;
            },
            []
        );
    }

    private preprocessBenefitTypes() {
        const benefitTypes = this.finalBenefitTypes.reduce((acc, value, index) => {
            const benefitTypeValue = value
                .replace(/ |_/gi, '')
                .toLowerCase() as keyof IVtoSkinCareTranslations;
            const translations = this.translations;
            const translationKey = Object.keys(translations).find(
                (key) => key.toLowerCase().indexOf(benefitTypeValue) !== -1
            ) as keyof IVtoSkinCareTranslations;

            const newBenefitTypeValue =
                typeof translationKey !== 'undefined'
                    ? translations[translationKey]
                    : benefitTypeValue;

            return { ...acc, [this.finalProductIds[index]]: newBenefitTypeValue };
        }, {} as IProductBenefitTypes);

        this.skinCareResultProductBenefitTypes = benefitTypes;
    }

    private handleScroll = () => {
        this.debounce(this.handleSeeResultButtonOnScroll, Constants.SCROLL_TIMEOUT);
    };

    private debounce(callback: () => void, timeoutValue: number) {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }
        this.debounceTimeout = setTimeout(callback, timeoutValue);
    }

    private handleSeeResultButtonOnScroll = () => {
        const vtoSkinCareRef = this.vtoSkinCareRef && this.vtoSkinCareRef.current;
        if (!vtoSkinCareRef) {
            return;
        }
        const offset = vtoSkinCareRef.getBoundingClientRect().bottom + Constants.OFFSET_HEIGHT;
        const isFixed = offset > screen.height;
        this.toggleResultCtaFixedPosition(isFixed);
    };

    public handleUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
    }

    public vtoSkinCareNodesLoaded = () => {
        emit(events.VTO_SKIN_CARE_RESULT_LOADED);
    };

    public vtoSkinCareResultLoaded = (productGridRef: React.RefObject<HTMLDivElement>) => {
        this.productGridRef = productGridRef;
        this.observeProductGrid();
    };

    private observeProductGrid() {
        const productGridContainer = this.productGridRef.current;
        if (productGridContainer === null) {
            return;
        }
        const mutationObserver: IMutationObserver = new MutationObserver(this.onProductGridLoaded);
        const config = {
            childList: true,
            subtree: true
        };

        mutationObserver.observe(productGridContainer, config);
    }

    private onProductGridLoaded = (
        mutationList: MutationRecord[],
        mutationObserver: IMutationObserver
    ) => {
        mutationList.forEach((mutation) => {
            if (mutation.type === 'childList') {
                this.preprocessSkinCareNodeResult();
                mutationObserver.disconnect();
            }
        });
    };
}
